import React, { useState, useEffect } from 'react';
import { ChevronDown, BarChart3 } from 'lucide-react';
import { useJugadoresEquipoActivo } from '../hooks/useJugadoresEquipoActivo';
import { useEstadisticas } from '../hooks/useEstadisticas';
import { EstadisticasJugadorEquipo } from '../services/EstadisticasService';
import ModalListaPartidos from '../components/modals/ModalListaPartidos';

/**
 * Tipos de métricas disponibles para las estadísticas
 */
type TipoMetrica = 'porcentaje_minutos' | 'promedio_minutos' | 'titularidades';

/**
 * Página de estadísticas del equipo
 * Muestra estadísticas de jugadores con diferentes métricas y barras de progreso
 */
const Estadisticas: React.FC = () => {
  const { equipo, temporada, loading: equipoLoading } = useJugadoresEquipoActivo();
  const { estadisticasEquipo, loading, error, obtenerEstadisticasEquipo } = useEstadisticas();
  
  // Estados
  const [metricaSeleccionada, setMetricaSeleccionada] = useState<TipoMetrica>('porcentaje_minutos');
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [animarBarras, setAnimarBarras] = useState(false);
  
  // Estado para el modal de lista de partidos
  const [selectedJugador, setSelectedJugador] = useState<{id: number, nombre: string} | null>(null);
  const [isModalPartidosOpen, setIsModalPartidosOpen] = useState(false);

  /**
   * Opciones del menú desplegable de métricas
   */
  const opcionesMetricas = [
    { value: 'porcentaje_minutos', label: 'Porcentaje de minutos' },
    { value: 'promedio_minutos', label: 'Promedio de minutos' },
    { value: 'titularidades', label: 'Titularidades' }
  ];

  /**
   * Obtiene las estadísticas del equipo
   */
  const cargarEstadisticas = async () => {
    if (!equipo?.id || !temporada?.id) return;
    
    try {
      await obtenerEstadisticasEquipo(equipo.id, temporada.id);
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
    }
  };

  /**
   * Ordena las estadísticas según la métrica seleccionada
   */
  const obtenerEstadisticasOrdenadas = (): EstadisticasJugadorEquipo[] => {
    return [...estadisticasEquipo].sort((a, b) => {
      switch (metricaSeleccionada) {
        case 'porcentaje_minutos':
          return b.porcentajeMinutosTotales - a.porcentajeMinutosTotales;
        case 'promedio_minutos':
          return b.promedioMinutosPorPartido - a.promedioMinutosPorPartido;
        case 'titularidades':
          return b.titularidades - a.titularidades;
        default:
          return 0;
      }
    });
  };

  /**
   * Obtiene el valor de la métrica seleccionada para un jugador
   */
  const obtenerValorMetrica = (jugador: EstadisticasJugadorEquipo): number => {
    switch (metricaSeleccionada) {
      case 'porcentaje_minutos':
        return jugador.porcentajeMinutosTotales;
      case 'promedio_minutos':
        return jugador.promedioMinutosPorPartido;
      case 'titularidades':
        return jugador.titularidades;
      default:
        return 0;
    }
  };

  /**
   * Obtiene el texto de la métrica seleccionada
   */
  const obtenerTextoMetrica = (valor: number): string => {
    switch (metricaSeleccionada) {
      case 'porcentaje_minutos':
        return `${valor.toFixed(1)}%`;
      case 'promedio_minutos':
        return `${Math.round(valor)} min`;
      case 'titularidades':
        return `${valor} partidos`;
      default:
        return valor.toString();
    }
  };

  /**
   * Calcula el porcentaje para la barra de progreso
   */
  const calcularPorcentajeBarra = (valor: number): number => {
    if (estadisticasEquipo.length === 0) return 0;
    
    const valores = estadisticasEquipo.map(obtenerValorMetrica);
    const maximo = Math.max(...valores);
    
    return maximo > 0 ? (valor / maximo) * 100 : 0;
  };

  // Activar animación después del renderizado inicial
  useEffect(() => {
    if (estadisticasEquipo.length > 0) {
      const timer = setTimeout(() => setAnimarBarras(true), 500);
      return () => clearTimeout(timer);
    }
  }, [estadisticasEquipo]);

  /**
   * Maneja el cambio de métrica
   */
  const handleCambioMetrica = (nuevaMetrica: TipoMetrica) => {
    setAnimarBarras(false);
    setMetricaSeleccionada(nuevaMetrica);
    setMostrarDropdown(false);
    setTimeout(() => setAnimarBarras(true), 50);
  };

  /**
   * Maneja el click en un jugador para ver sus partidos
   */
  const handleJugadorClick = (jugador: EstadisticasJugadorEquipo) => {
    setSelectedJugador({ 
      id: jugador.jugadorId, 
      nombre: `${jugador.nombre} ${jugador.apellido}` 
    });
    setIsModalPartidosOpen(true);
  };

  /**
   * Cierra el modal de partidos
   */
  const handleCloseModal = () => {
    setIsModalPartidosOpen(false);
    setSelectedJugador(null);
  };

  useEffect(() => {
    if (equipo?.id && temporada?.id) {
      cargarEstadisticas();
    }
  }, [equipo?.id, temporada?.id]);

  if (equipoLoading || loading) {
    return (
      <div className="flex-1 p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary-700 rounded w-3/4"></div>
          <div className="h-4 bg-secondary-700 rounded w-1/2"></div>
          <div className="h-12 bg-secondary-700 rounded"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-secondary-700 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-4">
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 text-center">
          <p className="text-red-400">Error al cargar estadísticas: {error}</p>
          <button 
            onClick={cargarEstadisticas}
            className="mt-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const estadisticasOrdenadas = obtenerEstadisticasOrdenadas();
  const metricaActual = opcionesMetricas.find(o => o.value === metricaSeleccionada);

  return (
    <div className="flex-1 p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <div className="flex items-center">
            <BarChart3 className="h-6 w-6 text-primary-500 mr-2" />
            <h1 className="text-xl font-semibold text-white">
              Estadísticas
            </h1>
          </div>
        </div>
        <p className="text-secondary-400 text-sm">
          {equipo?.nombre} - {temporada?.nombre}
        </p>
      </div>

      {/* Selector de métrica */}
      <div className="mb-6">
        <div className="relative">
          <button
            onClick={() => setMostrarDropdown(!mostrarDropdown)}
            className="w-full bg-secondary-800 border border-secondary-700 rounded-lg px-4 py-3 text-left text-white flex items-center justify-between hover:bg-secondary-700 transition-colors"
          >
            <span>{metricaActual?.label}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${mostrarDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          {mostrarDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-secondary-800 border border-secondary-700 rounded-lg shadow-lg z-50">
              {opcionesMetricas.map((opcion) => (
                <button
                  key={opcion.value}
                  onClick={() => handleCambioMetrica(opcion.value as TipoMetrica)}
                  className={`w-full px-4 py-3 text-left hover:bg-secondary-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    metricaSeleccionada === opcion.value ? 'text-primary-500 bg-secondary-700' : 'text-white'
                  }`}
                >
                  {opcion.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lista de estadísticas */}
      <div className="space-y-3">
        {estadisticasOrdenadas.length === 0 ? (
          <div className="bg-secondary-800 rounded-lg p-6 text-center border border-secondary-700">
            <p className="text-secondary-400">No hay estadísticas disponibles</p>
          </div>
        ) : (
          estadisticasOrdenadas.map((jugador, index) => {
            const valor = obtenerValorMetrica(jugador);
            const porcentajeBarra = calcularPorcentajeBarra(valor);
            
            return (
              <div 
                key={jugador.jugadorId} 
                onClick={() => handleJugadorClick(jugador)}
                className="bg-secondary-800 rounded-lg border border-secondary-700 overflow-hidden cursor-pointer hover:border-primary-500/50 transition-colors active:scale-[0.99] transform"
              >
                <div className="relative p-4">
                  {/* Barra de progreso de fondo */}
                  <div 
                    className="absolute inset-0 bg-primary-500/20 transition-all duration-500 ease-out"
                    style={{ width: animarBarras ? `${porcentajeBarra}%` : '0%' }}
                  ></div>
                  
                  {/* Contenido superpuesto */}
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      {/* Posición */}
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                        {index + 1}
                      </div>
                      
                      {/* Información del jugador */}
                      <div className="flex-1">
                        <div className="text-white font-medium">
                          {jugador.nombre} {jugador.apellido}
                        </div>
                        <div className="text-secondary-400 text-sm">
                          {jugador.totalJornadas} partidos jugados
                        </div>
                      </div>
                      
                      {/* Valor de la métrica */}
                      <div className="text-right">
                        <div className="text-lg font-bold text-primary-500">
                          {obtenerTextoMetrica(valor)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de lista de partidos */}
      {selectedJugador && (
        <ModalListaPartidos
          isOpen={isModalPartidosOpen}
          onClose={handleCloseModal}
          jugadorId={selectedJugador.id}
          jugadorNombre={selectedJugador.nombre}
          temporadaId={temporada?.id || 0}
        />
      )}
    </div>
  );
};

export default Estadisticas;